import useAxios, { IApiResponse } from "src/core/shared/hooks/useAxios";
import {
  PaginatedParams,
  FilterParams
} from "src/core/shared/api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  IError,
  IListErrorData
} from "src/core/errors/api/errors.api";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import { IProduct } from "src/core/products/api/products.api";

type ListApi =
  | "CREATE_ORDER_FROM_LIST"
  | "GET_DROP_LISTS"
  | "GET_LIST"
  | "GET_LISTS"
  | "CREATE_LIST"
  | "UPDATE_LIST"
  | "DELETE_LIST"
  | "CREATE_LIST_FROM_ORDER"
  | "PRE_UPDATE_LIST";

export type ListListsParams = { pagination: PaginatedParams<IList>; filters: FilterParams<IList> };
export type DropListParams = { pagination: PaginatedParams<IList>; filters: FilterParams<Pick<IList, "createdAt" | "updatedAt" | "name">> };
export type GetDropListsParams = { dropId: number, params: DropListParams };
export type CreateListParams = Pick<IList, "branchId" | "name" | "listLines" | "requestedDeliveryTime">;
export type CreateListFromOrderParams = { listName: string; id: number };
export type CreateOrderFromListParams = { id: number; deliveryDate: Date; editBeforePlacing: boolean; customerReference?: string };
export type SubmitListParams = IList;
export type UpdateListParams = Partial<IList> & Pick<IList, "id">;

export const listsApi = <R, P = any>(name: ListApi): [IApiResponse<R, any>, <C = P>(P: C) => void] => {
  switch (name) {
    case "CREATE_ORDER_FROM_LIST":
      return useAxios<R, CreateOrderFromListParams>((data: CreateOrderFromListParams) => {
        const listId = data.id;

        return {
          url: `${process.env.REACT_APP_API_URL}/core/lists/${listId}/to-order`,
          method: "POST",
          data: {
            deliveryDate: data.deliveryDate,
            customerReference: data.customerReference,
            editBeforePlacing: data.editBeforePlacing
          }
        };
      }, name);

    case "CREATE_LIST_FROM_ORDER":
      return useAxios<R, CreateListFromOrderParams>((data: CreateListFromOrderParams) => {
        const orderId = data.id;

        return {
          url: `${process.env.REACT_APP_API_URL}/core/lists/from-order/${orderId}`,
          method: "POST",
          data: { listName: data.listName }
        };
      }, name);

    case "GET_DROP_LISTS":
      return useAxios<R, DropListParams>(
        (data: GetDropListsParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists/branch/${data.dropId}`,
          method: "GET",
          params: data.params
        }),
        name
      );

    case "GET_LIST":
      return useAxios<R>(
        listId => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists/${listId}`,
          method: "GET"
        }),
        name
      );

    case "GET_LISTS":
      return useAxios<R>(
        (params: ListListsParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists`,
          method: "GET",
          params
        }),
        name
      );

    case "PRE_UPDATE_LIST":
      return useAxios<R>(
        (params: { listId: number }) => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists/pre-update/info`,
          method: "GET",
          params
        }),
        name
      );

    case "CREATE_LIST":
      return useAxios<R>(
        (data: CreateListParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists/`,
          method: "POST",
          data
        }),
        name
      );

    case "UPDATE_LIST":
      return useAxios<R>(
        (data: UpdateListParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists`,
          method: "PATCH",
          data
        }),
        name
      );

    case "DELETE_LIST":
      return useAxios<R>(
        listId => ({
          url: `${process.env.REACT_APP_API_URL}/core/lists/${listId}`,
          method: "DELETE"
        }),
        name
      );
    default:
      throw new Error("listsApi requires a name parameter");
  }
};

export type IList = {
  id: number;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  branchId: number;
  listLines: IListLine[];
  listNotes: IListNote[];
  branch: IDrop;
  errors?: Array<IError<IListErrorData>>;
  customerReference?: string;
  schedule?: ISchedule;
  deleted: boolean;
  hasDetachedOrders?: boolean;
  requestedDeliveryTime?: string | null;
};

export type IListKeys = Omit<IList, "listLines" | "schedule">;

export interface IListLine {
  id?: number;
  listId?: number;
  price: number | null;
  totalPrice?: number | null;
  productId: number;
  product: IProduct;
  quantity: number;
  totalVatValue: number | null;
  vatPercentage: number | null;
}

export interface IListNote {
  id?: number;
  orderId: number;
  body: string;
  authorId: number;
  source: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ListPreRequestUpdate {
  toUpdate: number;
}
